import {
  PBRMaterial, Color3, Texture, CubeTexture,
  ProceduralTexture, DynamicTexture, Constants,
  ShaderMaterial, Effect,
} from '@babylonjs/core';

/**
 * Creates a running-bond paver pattern (offset rows) on a DynamicTexture.
 */
function createPaverTexture(name, scene, paverW, paverH, baseColor, groutColor, size = 512) {
  const tex = new DynamicTexture(name, size, scene, true);
  const ctx = tex.getContext();
  const grout = 3;

  // Fill grout
  ctx.fillStyle = groutColor;
  ctx.fillRect(0, 0, size, size);

  // Running bond pattern (offset every other row)
  for (let row = 0; row * (paverH + grout) < size; row++) {
    const yy = row * (paverH + grout);
    const offset = (row % 2) * (paverW / 2);
    const r = parseInt(baseColor.substring(1, 3), 16);
    const g = parseInt(baseColor.substring(3, 5), 16);
    const b = parseInt(baseColor.substring(5, 7), 16);
    for (let xx = -paverW; xx < size + paverW; xx += paverW + grout) {
      const v = Math.random() * 15 - 7;
      ctx.fillStyle = `rgb(${r + v},${g + v},${b + v})`;
      ctx.fillRect(xx + offset, yy, paverW, paverH);
    }
  }
  tex.update();
  tex.wrapU = Constants.TEXTURE_WRAP_ADDRESSMODE;
  tex.wrapV = Constants.TEXTURE_WRAP_ADDRESSMODE;
  return tex;
}

/**
 * Creates a cross-brick / basketweave paver texture.
 * Square cells alternate between horizontal and vertical brick pairs.
 * @param {number} brickL — long side of each brick
 * @param {number} n — number of bricks per cell (default 2)
 */
function createCrossBrickTexture(name, scene, brickL, n, baseColor, groutColor, size = 512) {
  const tex = new DynamicTexture(name, size, scene, true);
  const ctx = tex.getContext();
  const g = 3; // grout width

  // Fill grout background
  ctx.fillStyle = groutColor;
  ctx.fillRect(0, 0, size, size);

  const r = parseInt(baseColor.substring(1, 3), 16);
  const gr = parseInt(baseColor.substring(3, 5), 16);
  const b = parseInt(baseColor.substring(5, 7), 16);

  // Each cell is a square of side brickL
  // Contains n bricks: brickW = (brickL - (n-1)*g) / n
  const brickW = (brickL - (n - 1) * g) / n;
  const cellStep = brickL + g;

  for (let row = -1; row * cellStep < size + cellStep; row++) {
    for (let col = -1; col * cellStep < size + cellStep; col++) {
      const cx = col * cellStep;
      const cy = row * cellStep;
      const isHoriz = (row + col) % 2 === 0;

      for (let i = 0; i < n; i++) {
        const v = Math.random() * 18 - 9;
        ctx.fillStyle = `rgb(${Math.max(0, Math.min(255, r + v))},${Math.max(0, Math.min(255, gr + v))},${Math.max(0, Math.min(255, b + v))})`;

        if (isHoriz) {
          // n horizontal bricks stacked vertically
          ctx.fillRect(cx, cy + i * (brickW + g), brickL, brickW);
        } else {
          // n vertical bricks side by side
          ctx.fillRect(cx + i * (brickW + g), cy, brickW, brickL);
        }
      }
    }
  }

  tex.update();
  tex.wrapU = Constants.TEXTURE_WRAP_ADDRESSMODE;
  tex.wrapV = Constants.TEXTURE_WRAP_ADDRESSMODE;
  return tex;
}

/**
 * Creates a rough asphalt noise texture (grainy dark surface, no pattern).
 */
function createAsphaltTexture(name, scene, baseColor, size = 512) {
  const tex = new DynamicTexture(name, size, scene, true);
  const ctx = tex.getContext();
  const r = parseInt(baseColor.substring(1, 3), 16);
  const g = parseInt(baseColor.substring(3, 5), 16);
  const b = parseInt(baseColor.substring(5, 7), 16);

  // Fill base
  ctx.fillStyle = baseColor;
  ctx.fillRect(0, 0, size, size);

  // Coarse aggregate noise
  for (let i = 0; i < 8000; i++) {
    const px = Math.random() * size;
    const py = Math.random() * size;
    const v = Math.random() * 30 - 15;
    const a = 0.3 + Math.random() * 0.4;
    ctx.fillStyle = `rgba(${Math.max(0, r + v)},${Math.max(0, g + v)},${Math.max(0, b + v)},${a})`;
    const s = Math.random() * 3 + 1;
    ctx.fillRect(px, py, s, s);
  }

  // Fine speckle
  for (let i = 0; i < 4000; i++) {
    const px = Math.random() * size;
    const py = Math.random() * size;
    const bright = Math.random() * 20 - 10;
    ctx.fillStyle = `rgba(${Math.max(0, r + bright)},${Math.max(0, g + bright)},${Math.max(0, b + bright)},0.5)`;
    ctx.fillRect(px, py, 1, 1);
  }

  tex.update();
  tex.wrapU = Constants.TEXTURE_WRAP_ADDRESSMODE;
  tex.wrapV = Constants.TEXTURE_WRAP_ADDRESSMODE;
  return tex;
}

/**
 * Creates a cobblestone / pebble mosaic texture — square grid cells
 * filled with irregular rounded stones in varied earthy tones.
 */
function createCobblestoneTexture(name, scene, cellCount, size = 512) {
  const tex = new DynamicTexture(name, size, scene, true);
  const ctx = tex.getContext();

  const groutW = 4;
  const cellSize = (size - groutW) / cellCount;

  // Stone color palette — earthy tones
  const palette = [
    '#a89888', '#bfab94', '#c8b8a0', '#8a7e70',
    '#d4c4aa', '#9e8e78', '#b0a090', '#c0b098',
    '#8c8074', '#a09484', '#b8a890', '#ccc0a8',
    '#988c7c', '#a49888', '#bca88e', '#7a706a',
  ];

  // Fill grout background (gray mortar)
  ctx.fillStyle = '#6e6862';
  ctx.fillRect(0, 0, size, size);

  // Draw cells with pebbles
  for (let row = 0; row < cellCount; row++) {
    for (let col = 0; col < cellCount; col++) {
      const cx = col * cellSize + groutW;
      const cy = row * cellSize + groutW;
      const innerW = cellSize - groutW;
      const innerH = cellSize - groutW;

      // Fill cell background slightly lighter than grout
      ctx.fillStyle = '#787068';
      ctx.fillRect(cx, cy, innerW, innerH);

      // Pack irregular pebbles into the cell
      const stoneR = innerW * 0.08; // base stone radius
      const stones = [];
      const attempts = 300;
      for (let a = 0; a < attempts; a++) {
        const sr = stoneR * (0.6 + Math.random() * 0.8);
        const sx = cx + sr + Math.random() * (innerW - sr * 2);
        const sy = cy + sr + Math.random() * (innerH - sr * 2);

        // Check overlap
        let overlap = false;
        for (const s of stones) {
          const dx = sx - s.x, dy = sy - s.y;
          if (Math.sqrt(dx * dx + dy * dy) < sr + s.r + 1) { overlap = true; break; }
        }
        if (overlap) continue;
        stones.push({ x: sx, y: sy, r: sr });
      }

      // Draw each stone
      for (const s of stones) {
        const color = palette[Math.floor(Math.random() * palette.length)];
        const cr = parseInt(color.substring(1, 3), 16);
        const cg = parseInt(color.substring(3, 5), 16);
        const cb = parseInt(color.substring(5, 7), 16);
        const v = Math.random() * 20 - 10;

        // Stone body — slightly irregular ellipse
        ctx.beginPath();
        const rx = s.r * (0.85 + Math.random() * 0.3);
        const ry = s.r * (0.85 + Math.random() * 0.3);
        const rot = Math.random() * Math.PI;
        ctx.ellipse(s.x, s.y, rx, ry, rot, 0, Math.PI * 2);
        ctx.fillStyle = `rgb(${Math.max(0, Math.min(255, cr + v))},${Math.max(0, Math.min(255, cg + v))},${Math.max(0, Math.min(255, cb + v))})`;
        ctx.fill();

        // Highlight (top-left)
        ctx.beginPath();
        ctx.ellipse(s.x - rx * 0.2, s.y - ry * 0.2, rx * 0.5, ry * 0.5, rot, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,0.12)`;
        ctx.fill();

        // Shadow edge (bottom-right)
        ctx.beginPath();
        ctx.ellipse(s.x + rx * 0.15, s.y + ry * 0.15, rx * 0.9, ry * 0.9, rot, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(0,0,0,0.1)`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }
    }
  }

  tex.update();
  tex.wrapU = Constants.TEXTURE_WRAP_ADDRESSMODE;
  tex.wrapV = Constants.TEXTURE_WRAP_ADDRESSMODE;
  return tex;
}

/**
 * Creates an octagon-and-square tile texture (white/gray).
 * Large octagons with small accent squares at corners, with subtle surface mottling.
 */
function createOctagonTileTexture(name, scene, cellSize, size = 512) {
  const tex = new DynamicTexture(name, size, scene, true);
  const ctx = tex.getContext();
  const grout = 3;

  // Small corner cut — 15% of cell so octagons are clearly dominant,
  // accent squares are small like in the reference image
  const d = Math.round(cellSize * 0.15);

  // Fill grout background — dark for visibility through glass
  ctx.fillStyle = '#555050';
  ctx.fillRect(0, 0, size, size);

  const count = Math.ceil(size / cellSize) + 2;

  // Draw octagons
  for (let row = -1; row < count; row++) {
    for (let col = -1; col < count; col++) {
      const ox = col * cellSize;
      const oy = row * cellSize;
      const g = grout;

      // Octagon: 8 vertices (square with small clipped corners)
      ctx.beginPath();
      ctx.moveTo(ox + d + g,            oy + g);
      ctx.lineTo(ox + cellSize - d - g,  oy + g);
      ctx.lineTo(ox + cellSize - g,      oy + d + g);
      ctx.lineTo(ox + cellSize - g,      oy + cellSize - d - g);
      ctx.lineTo(ox + cellSize - d - g,  oy + cellSize - g);
      ctx.lineTo(ox + d + g,            oy + cellSize - g);
      ctx.lineTo(ox + g,                oy + cellSize - d - g);
      ctx.lineTo(ox + g,                oy + d + g);
      ctx.closePath();

      // White/light gray with per-tile variation
      const v = Math.random() * 12 - 6;
      const base = 228 + v;
      ctx.fillStyle = `rgb(${base|0},${base|0},${(base - 2)|0})`;
      ctx.fill();

      // Subtle surface mottling
      const cx = ox + cellSize / 2, cy = oy + cellSize / 2;
      const mr = cellSize * 0.3;
      const grad = ctx.createRadialGradient(
        cx + (Math.random() - 0.5) * mr, cy + (Math.random() - 0.5) * mr, 0,
        cx, cy, mr
      );
      const mottleA = 0.04 + Math.random() * 0.06;
      grad.addColorStop(0, `rgba(200,200,198,${mottleA})`);
      grad.addColorStop(1, 'rgba(200,200,198,0)');
      ctx.fillStyle = grad;
      ctx.fill();
    }
  }

  // Draw small accent squares (rotated 45°) at grid intersections
  for (let row = 0; row < count; row++) {
    for (let col = 0; col < count; col++) {
      const cx = col * cellSize;
      const cy = row * cellSize;
      const half = d - grout;

      // Diamond (square rotated 45°)
      ctx.beginPath();
      ctx.moveTo(cx,        cy - half);
      ctx.lineTo(cx + half, cy);
      ctx.lineTo(cx,        cy + half);
      ctx.lineTo(cx - half, cy);
      ctx.closePath();

      // Dark gray accent for visibility
      const sv = Math.random() * 12 - 6;
      const sg = 105 + sv;
      ctx.fillStyle = `rgb(${sg|0},${sg|0},${(sg - 2)|0})`;
      ctx.fill();
    }
  }

  tex.update();
  tex.wrapU = Constants.TEXTURE_WRAP_ADDRESSMODE;
  tex.wrapV = Constants.TEXTURE_WRAP_ADDRESSMODE;
  return tex;
}

/**
 * Creates a procedural checker/tile texture on a DynamicTexture.
 */
function createTileTexture(name, scene, tileW, tileH, baseColor, groutColor, size = 512) {
  const tex = new DynamicTexture(name, size, scene, true);
  const ctx = tex.getContext();
  const grout = 2;

  // Fill grout
  ctx.fillStyle = groutColor;
  ctx.fillRect(0, 0, size, size);

  // Draw tiles
  ctx.fillStyle = baseColor;
  for (let x = 0; x < size; x += tileW + grout) {
    for (let y = 0; y < size; y += tileH + grout) {
      // Slight color variation per tile
      const r = parseInt(baseColor.substring(1, 3), 16);
      const g = parseInt(baseColor.substring(3, 5), 16);
      const b = parseInt(baseColor.substring(5, 7), 16);
      const v = Math.random() * 12 - 6;
      ctx.fillStyle = `rgb(${r + v},${g + v},${b + v})`;
      ctx.fillRect(x, y, tileW, tileH);
    }
  }
  tex.update();
  return tex;
}

/**
 * Creates a procedural wood grain texture.
 */
function createWoodTexture(name, scene, size = 512) {
  const tex = new DynamicTexture(name, size, scene, true);
  const ctx = tex.getContext();

  // Base wood color
  ctx.fillStyle = '#8B6914';
  ctx.fillRect(0, 0, size, size);

  // Grain lines
  for (let i = 0; i < 120; i++) {
    const y = Math.random() * size;
    const w = Math.random() * 2 + 0.5;
    const alpha = Math.random() * 0.25 + 0.05;
    ctx.strokeStyle = `rgba(60, 30, 5, ${alpha})`;
    ctx.lineWidth = w;
    ctx.beginPath();
    ctx.moveTo(0, y);
    // Wavy line
    for (let x = 0; x < size; x += 10) {
      ctx.lineTo(x, y + Math.sin(x * 0.02 + i) * 3);
    }
    ctx.stroke();
  }

  // Knots
  for (let i = 0; i < 3; i++) {
    const kx = Math.random() * size;
    const ky = Math.random() * size;
    const kr = Math.random() * 15 + 5;
    const grad = ctx.createRadialGradient(kx, ky, 0, kx, ky, kr);
    grad.addColorStop(0, 'rgba(50, 25, 5, 0.6)');
    grad.addColorStop(1, 'rgba(50, 25, 5, 0)');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.ellipse(kx, ky, kr, kr * 0.6, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  tex.update();
  return tex;
}

/**
 * Creates a procedural marble texture.
 */
function createMarbleTexture(name, scene, size = 512) {
  const tex = new DynamicTexture(name, size, scene, true);
  const ctx = tex.getContext();

  // White base
  ctx.fillStyle = '#f0ece4';
  ctx.fillRect(0, 0, size, size);

  // Veins
  for (let i = 0; i < 15; i++) {
    ctx.strokeStyle = `rgba(${140 + Math.random() * 40}, ${130 + Math.random() * 30}, ${120 + Math.random() * 20}, ${0.15 + Math.random() * 0.2})`;
    ctx.lineWidth = Math.random() * 3 + 0.5;
    ctx.beginPath();
    let x = Math.random() * size;
    let y = 0;
    ctx.moveTo(x, y);
    while (y < size) {
      x += (Math.random() - 0.5) * 40;
      y += Math.random() * 30 + 10;
      ctx.lineTo(x, y);
    }
    ctx.stroke();
  }

  // Subtle noise overlay
  for (let i = 0; i < 2000; i++) {
    const px = Math.random() * size;
    const py = Math.random() * size;
    const alpha = Math.random() * 0.04;
    ctx.fillStyle = `rgba(100, 90, 80, ${alpha})`;
    ctx.fillRect(px, py, 2, 2);
  }

  tex.update();
  return tex;
}

/**
 * Creates a cool gray-blue marble texture with dark organic veins and blotchy patches.
 */
function createGrayMarbleTexture(name, scene, size = 512) {
  const tex = new DynamicTexture(name, size, scene, true);
  const ctx = tex.getContext();

  // Cool gray-blue base
  ctx.fillStyle = '#b8bcc4';
  ctx.fillRect(0, 0, size, size);

  // Soft blotchy patches (lighter and darker regions)
  for (let i = 0; i < 20; i++) {
    const px = Math.random() * size;
    const py = Math.random() * size;
    const radius = 40 + Math.random() * 80;
    const grad = ctx.createRadialGradient(px, py, 0, px, py, radius);
    const light = Math.random() > 0.5;
    if (light) {
      grad.addColorStop(0, `rgba(210, 215, 225, ${0.2 + Math.random() * 0.2})`);
      grad.addColorStop(1, 'rgba(210, 215, 225, 0)');
    } else {
      grad.addColorStop(0, `rgba(100, 105, 115, ${0.15 + Math.random() * 0.15})`);
      grad.addColorStop(1, 'rgba(100, 105, 115, 0)');
    }
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, size, size);
  }

  // Dark veins — thick organic branching
  for (let i = 0; i < 10; i++) {
    const alpha = 0.15 + Math.random() * 0.25;
    const gray = 50 + Math.random() * 40;
    ctx.strokeStyle = `rgba(${gray}, ${gray + 5}, ${gray + 10}, ${alpha})`;
    ctx.lineWidth = Math.random() * 4 + 1;
    ctx.beginPath();
    let x = Math.random() * size;
    let y = 0;
    ctx.moveTo(x, y);
    while (y < size) {
      x += (Math.random() - 0.5) * 50;
      y += Math.random() * 25 + 8;
      ctx.lineTo(x, y);
    }
    ctx.stroke();

    // Thinner branch veins forking off
    if (Math.random() > 0.4) {
      ctx.lineWidth = Math.random() * 1.5 + 0.3;
      ctx.strokeStyle = `rgba(${gray}, ${gray + 5}, ${gray + 10}, ${alpha * 0.6})`;
      const bx = x - (Math.random() - 0.5) * 60;
      const by = y * Math.random();
      ctx.beginPath();
      ctx.moveTo(bx, by);
      let bx2 = bx, by2 = by;
      for (let j = 0; j < 8; j++) {
        bx2 += (Math.random() - 0.5) * 35;
        by2 += Math.random() * 20 + 5;
        ctx.lineTo(bx2, by2);
      }
      ctx.stroke();
    }
  }

  // Fine white veins (subtle)
  for (let i = 0; i < 6; i++) {
    ctx.strokeStyle = `rgba(220, 225, 235, ${0.08 + Math.random() * 0.1})`;
    ctx.lineWidth = Math.random() * 1.5 + 0.3;
    ctx.beginPath();
    let x = Math.random() * size;
    let y = 0;
    ctx.moveTo(x, y);
    while (y < size) {
      x += (Math.random() - 0.5) * 30;
      y += Math.random() * 35 + 10;
      ctx.lineTo(x, y);
    }
    ctx.stroke();
  }

  // Subtle noise
  for (let i = 0; i < 3000; i++) {
    const px = Math.random() * size;
    const py = Math.random() * size;
    const a = Math.random() * 0.04;
    const v = Math.random() > 0.5 ? '180,185,195' : '90,95,105';
    ctx.fillStyle = `rgba(${v}, ${a})`;
    ctx.fillRect(px, py, 1.5, 1.5);
  }

  tex.update();
  tex.wrapU = Constants.TEXTURE_WRAP_ADDRESSMODE;
  tex.wrapV = Constants.TEXTURE_WRAP_ADDRESSMODE;
  return tex;
}

/**
 * Creates a normal map approximation from a dynamic texture for bump effect.
 */
function createBumpFromTile(name, scene, tileW, tileH, size = 512) {
  const tex = new DynamicTexture(name, size, scene, true);
  const ctx = tex.getContext();
  const grout = 2;

  ctx.fillStyle = '#8080ff'; // Neutral normal (flat)
  ctx.fillRect(0, 0, size, size);

  // Grout lines (recessed = darker normal)
  ctx.fillStyle = '#7070f0';
  for (let x = 0; x < size; x += tileW + grout) {
    ctx.fillRect(x, 0, grout, size);
  }
  for (let y = 0; y < size; y += tileH + grout) {
    ctx.fillRect(0, y, size, grout);
  }

  // Tile edges (raised)
  ctx.fillStyle = '#9090ff';
  for (let x = 0; x < size; x += tileW + grout) {
    for (let y = 0; y < size; y += tileH + grout) {
      ctx.fillRect(x + grout, y + grout, tileW, 1);
      ctx.fillRect(x + grout, y + grout, 1, tileH);
    }
  }

  tex.update();
  return tex;
}

/**
 * Creates a palm trunk texture with horizontal ring marks (key visual feature).
 */
function createPalmTrunkTexture(name, scene, size = 512) {
  const tex = new DynamicTexture(name, size, scene, true);
  const ctx = tex.getContext();

  // Base fill: cool gray with slight green tint (lighter than deciduous bark)
  ctx.fillStyle = 'rgb(150, 145, 128)';
  ctx.fillRect(0, 0, size, size);

  // 4-6 radial gradient color patches for variation
  const patchCount = 4 + Math.floor(Math.random() * 3);
  for (let i = 0; i < patchCount; i++) {
    const px = Math.random() * size;
    const py = Math.random() * size;
    const radius = 50 + Math.random() * 60;
    const grad = ctx.createRadialGradient(px, py, 0, px, py, radius);
    const lighter = Math.random() > 0.5;
    if (lighter) {
      grad.addColorStop(0, `rgba(175, 170, 152, ${0.15 + Math.random() * 0.15})`);
      grad.addColorStop(1, 'rgba(175, 170, 152, 0)');
    } else {
      // Slight green tint patches (typical of young palm trunks)
      grad.addColorStop(0, `rgba(120, 125, 100, ${0.15 + Math.random() * 0.15})`);
      grad.addColorStop(1, 'rgba(120, 125, 100, 0)');
    }
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, size, size);
  }

  // 30-45 horizontal ring marks — THE defining visual feature of palm trunks
  const ringCount = 30 + Math.floor(Math.random() * 16);
  const ringSpacing = size / ringCount;
  for (let i = 0; i < ringCount; i++) {
    const baseY = i * ringSpacing + (Math.random() - 0.5) * ringSpacing * 0.25;

    // Dark groove line — high contrast for visibility
    ctx.strokeStyle = `rgba(65, 60, 45, ${0.55 + Math.random() * 0.25})`;
    ctx.lineWidth = 2.5 + Math.random() * 1.5;
    ctx.beginPath();
    ctx.moveTo(0, baseY);
    for (let x = 0; x < size; x += 6) {
      ctx.lineTo(x, baseY + Math.sin(x * 0.012 + i) * 1.2);
    }
    ctx.stroke();

    // Bright highlight line below groove (lit edge of ring scar)
    ctx.strokeStyle = `rgba(195, 190, 170, ${0.35 + Math.random() * 0.15})`;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(0, baseY + 3.5);
    for (let x = 0; x < size; x += 6) {
      ctx.lineTo(x, baseY + 3.5 + Math.sin(x * 0.012 + i) * 1.2);
    }
    ctx.stroke();
  }

  // 5-8 faint vertical fiber streaks (very subtle)
  const fiberCount = 5 + Math.floor(Math.random() * 4);
  for (let i = 0; i < fiberCount; i++) {
    const x = Math.random() * size;
    ctx.strokeStyle = `rgba(130, 128, 108, ${0.05 + Math.random() * 0.05})`;
    ctx.lineWidth = 2 + Math.random() * 2;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    for (let y = 0; y < size; y += 6) {
      ctx.lineTo(x + Math.sin(y * 0.03 + i * 2) * 1.5, y);
    }
    ctx.stroke();
  }

  // 300-500 fine noise speckle dots
  const speckleCount = 300 + Math.floor(Math.random() * 201);
  for (let i = 0; i < speckleCount; i++) {
    const px = Math.random() * size;
    const py = Math.random() * size;
    const bright = Math.random() > 0.5;
    ctx.fillStyle = bright
      ? `rgba(170, 168, 150, ${Math.random() * 0.2})`
      : `rgba(115, 112, 95, ${Math.random() * 0.2})`;
    ctx.fillRect(px, py, 1.5, 1.5);
  }

  tex.update();
  tex.wrapU = Constants.TEXTURE_WRAP_ADDRESSMODE;
  tex.wrapV = Constants.TEXTURE_WRAP_ADDRESSMODE;
  return tex;
}

/**
 * Creates a palm trunk bump/normal map texture with ring structure.
 */
function createPalmTrunkBumpTexture(name, scene, size = 512) {
  const tex = new DynamicTexture(name, size, scene, true);
  const ctx = tex.getContext();

  // Fill with neutral normal #8080ff
  ctx.fillStyle = '#8080ff';
  ctx.fillRect(0, 0, size, size);

  // Horizontal ring grooves
  const ringCount = 25 + Math.floor(Math.random() * 16);
  const ringSpacing = size / ringCount;
  for (let i = 0; i < ringCount; i++) {
    const baseY = i * ringSpacing + (Math.random() - 0.5) * ringSpacing * 0.3;

    // Groove (recessed)
    ctx.strokeStyle = '#7575ec';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(0, baseY);
    for (let x = 0; x < size; x += 8) {
      ctx.lineTo(x, baseY + Math.sin(x * 0.015 + i) * 1.5);
    }
    ctx.stroke();

    // Ridge (raised) just below
    ctx.strokeStyle = '#8888ff';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, baseY + 3);
    for (let x = 0; x < size; x += 8) {
      ctx.lineTo(x, baseY + 3 + Math.sin(x * 0.015 + i) * 1.5);
    }
    ctx.stroke();
  }

  // Fine noise specks
  const speckCount = 200 + Math.floor(Math.random() * 101);
  for (let i = 0; i < speckCount; i++) {
    const px = Math.random() * size;
    const py = Math.random() * size;
    ctx.fillStyle = Math.random() > 0.5 ? '#8585ff' : '#7b7bf2';
    ctx.fillRect(px, py, 1.5, 1.5);
  }

  tex.update();
  tex.wrapU = Constants.TEXTURE_WRAP_ADDRESSMODE;
  tex.wrapV = Constants.TEXTURE_WRAP_ADDRESSMODE;
  return tex;
}

/**
 * Creates a single feather-shaped palm frond texture on transparent background.
 * Tapered feather shape — leaflets shorter at tip & base, full in the middle.
 * Brighter green palette matching tropical coconut palms.
 */
function createPalmFrondTexture(name, scene, size = 512) {
  const tex = new DynamicTexture(name, size, scene, true);
  const ctx = tex.getContext();

  // Transparent background
  ctx.clearRect(0, 0, size, size);

  const midX = size / 2;
  const topY = size * 0.02;
  const botY = size * 0.94;

  // Rachis (central spine) — tapering dark-green
  ctx.lineCap = 'round';
  ctx.strokeStyle = 'rgb(40, 100, 28)';
  ctx.lineWidth = 5;
  ctx.beginPath();
  ctx.moveTo(midX, botY);
  ctx.quadraticCurveTo(midX + 3, (topY + botY) / 2, midX, topY);
  ctx.stroke();
  ctx.strokeStyle = 'rgb(50, 115, 35)';
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.moveTo(midX, topY + (botY - topY) * 0.25);
  ctx.lineTo(midX, topY);
  ctx.stroke();

  // Dense leaflets — 55 pairs with feather-taper envelope
  // Dense leaflets with feather taper
  const leafletCount = 55;
  const leafletStartY = topY + (botY - topY) * 0.02;
  const leafletEndY = botY - (botY - topY) * 0.04;

  for (let i = 0; i < leafletCount; i++) {
    const t = i / leafletCount; // 0=tip, 1=base
    const y = leafletStartY + t * (leafletEndY - leafletStartY);

    // Skip every ~8th for subtle gaps
    if (i % 8 === 5) continue;

    // Feather-shape envelope: leaflets taper at tip AND base
    let envelope;
    if (t < 0.08) {
      envelope = t / 0.08;
    } else if (t < 0.20) {
      envelope = 0.7 + (t - 0.08) / 0.12 * 0.3;
    } else if (t > 0.90) {
      envelope = 1.0 - (t - 0.90) / 0.10;
    } else {
      envelope = 1.0;
    }

    const maxLeafletLen = size * 0.44;
    const leafletLen = maxLeafletLen * envelope * (0.90 + Math.random() * 0.1);

    // Angle from rachis: 25-45°
    const angle = (25 + Math.random() * 20) * Math.PI / 180;
    const rachisX = midX + Math.sin(t * 0.5) * 2;

    for (const side of [-1, 1]) {
      const r = 50 + Math.floor(Math.random() * 50);
      const g = 160 + Math.floor(Math.random() * 80);
      const b = 18 + Math.floor(Math.random() * 30);

      const endX = rachisX + side * Math.cos(angle) * leafletLen;
      const endYp = y - Math.sin(angle) * leafletLen;

      // Primary leaflet stroke (5-8px — visible at distance, not too heavy)
      ctx.strokeStyle = `rgb(${r}, ${g}, ${b})`;
      ctx.lineWidth = 5 + Math.random() * 3;
      ctx.beginPath();
      ctx.moveTo(rachisX, y);
      ctx.lineTo(endX, endYp);
      ctx.stroke();

      // Lighter highlight vein (wider)
      ctx.strokeStyle = `rgb(${Math.min(255, r + 30)}, ${Math.min(255, g + 25)}, ${Math.min(255, b + 15)})`;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(rachisX + side * 2, y + 1);
      ctx.lineTo(endX + side * 1, endYp + 0.5);
      ctx.stroke();

      // Dark edge for depth (wider)
      ctx.strokeStyle = `rgba(${Math.max(0, r - 25)}, ${Math.max(0, g - 30)}, ${Math.max(0, b - 15)}, 0.6)`;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(rachisX - side * 1, y - 0.5);
      ctx.lineTo(endX - side * 0.5, endYp - 0.5);
      ctx.stroke();
    }
  }

  tex.update();
  tex.hasAlpha = true;
  tex.wrapU = Constants.TEXTURE_WRAP_ADDRESSMODE;
  tex.wrapV = Constants.TEXTURE_WRAP_ADDRESSMODE;
  return tex;
}

/**
 * Creates a bark texture with vertical grooves, cracks, plate breaks, and speckle noise.
 */
function createBarkTexture(name, scene, size = 512) {
  const tex = new DynamicTexture(name, size, scene, true);
  const ctx = tex.getContext();

  // Base fill: brown
  ctx.fillStyle = 'rgb(82, 56, 31)';
  ctx.fillRect(0, 0, size, size);

  // 6-8 large radial gradient color patches for color variation
  const patchCount = 6 + Math.floor(Math.random() * 3);
  for (let i = 0; i < patchCount; i++) {
    const px = Math.random() * size;
    const py = Math.random() * size;
    const radius = 40 + Math.random() * 40;
    const grad = ctx.createRadialGradient(px, py, 0, px, py, radius);
    const lighter = Math.random() > 0.5;
    if (lighter) {
      grad.addColorStop(0, `rgba(110, 85, 55, ${0.15 + Math.random() * 0.15})`);
      grad.addColorStop(1, 'rgba(110, 85, 55, 0)');
    } else {
      grad.addColorStop(0, `rgba(40, 25, 12, ${0.15 + Math.random() * 0.15})`);
      grad.addColorStop(1, 'rgba(40, 25, 12, 0)');
    }
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, size, size);
  }

  // 60-80 vertical groove lines with sinusoidal waviness
  const grooveCount = 60 + Math.floor(Math.random() * 21);
  for (let i = 0; i < grooveCount; i++) {
    const x = Math.random() * size;
    const darkness = Math.random() * 40 + 15;
    ctx.strokeStyle = `rgba(${Math.max(0, 52 - darkness)}, ${Math.max(0, 36 - darkness / 2)}, 11, ${0.25 + Math.random() * 0.45})`;
    ctx.lineWidth = Math.random() * 2.5 + 0.5;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    for (let y = 0; y < size; y += 4) {
      ctx.lineTo(x + Math.sin(y * 0.05 + i) * (2 + Math.random() * 2), y);
    }
    ctx.stroke();
  }

  // 8-12 horizontal bark plate breaks (thick wavy lines)
  const plateCount = 8 + Math.floor(Math.random() * 5);
  for (let i = 0; i < plateCount; i++) {
    const y = Math.random() * size;
    ctx.strokeStyle = `rgba(35, 22, 10, ${0.3 + Math.random() * 0.2})`;
    ctx.lineWidth = 2 + Math.random() * 2;
    ctx.beginPath();
    ctx.moveTo(0, y);
    for (let x = 0; x < size; x += 8) {
      ctx.lineTo(x, y + Math.sin(x * 0.03 + i * 2) * 4);
    }
    ctx.stroke();
  }

  // 4-6 lighter vertical highlight streaks
  const streakCount = 4 + Math.floor(Math.random() * 3);
  for (let i = 0; i < streakCount; i++) {
    const x = Math.random() * size;
    ctx.strokeStyle = `rgba(110, 85, 55, ${0.15 + Math.random() * 0.1})`;
    ctx.lineWidth = 3 + Math.random() * 3;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    for (let y = 0; y < size; y += 6) {
      ctx.lineTo(x + Math.sin(y * 0.04 + i * 3) * 2, y);
    }
    ctx.stroke();
  }

  // 25-35 short horizontal cracks
  const crackCount = 25 + Math.floor(Math.random() * 11);
  for (let i = 0; i < crackCount; i++) {
    const cx = Math.random() * size;
    const cy = Math.random() * size;
    const len = Math.random() * 25 + 5;
    ctx.strokeStyle = `rgba(30, 20, 8, ${0.3 + Math.random() * 0.3})`;
    ctx.lineWidth = Math.random() * 1.5 + 0.5;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + len, cy + (Math.random() - 0.5) * 4);
    ctx.stroke();
  }

  // 500 speckle noise dots
  for (let i = 0; i < 500; i++) {
    const px = Math.random() * size;
    const py = Math.random() * size;
    const bright = Math.random() > 0.5;
    ctx.fillStyle = bright
      ? `rgba(100, 80, 50, ${Math.random() * 0.3})`
      : `rgba(30, 20, 10, ${Math.random() * 0.3})`;
    ctx.fillRect(px, py, 1.5, 1.5);
  }

  tex.update();
  tex.wrapU = Constants.TEXTURE_WRAP_ADDRESSMODE;
  tex.wrapV = Constants.TEXTURE_WRAP_ADDRESSMODE;
  return tex;
}

/**
 * Creates a bark bump/normal map texture for surface detail.
 */
function createBarkBumpTexture(name, scene, size = 512) {
  const tex = new DynamicTexture(name, size, scene, true);
  const ctx = tex.getContext();

  // Fill with neutral normal #8080ff
  ctx.fillStyle = '#8080ff';
  ctx.fillRect(0, 0, size, size);

  // Vertical groove recesses
  const grooveCount = 60 + Math.floor(Math.random() * 21);
  for (let i = 0; i < grooveCount; i++) {
    const x = Math.random() * size;
    ctx.strokeStyle = '#7070e8';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    for (let y = 0; y < size; y += 4) {
      ctx.lineTo(x + Math.sin(y * 0.05 + i) * 3, y);
    }
    ctx.stroke();

    // Raised edges next to grooves
    ctx.strokeStyle = '#9090ff';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x + 2, 0);
    for (let y = 0; y < size; y += 4) {
      ctx.lineTo(x + 2 + Math.sin(y * 0.05 + i) * 3, y);
    }
    ctx.stroke();
  }

  // Horizontal plate breaks
  const plateCount = 8 + Math.floor(Math.random() * 5);
  for (let i = 0; i < plateCount; i++) {
    const y = Math.random() * size;
    ctx.strokeStyle = '#7575ec';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, y);
    for (let x = 0; x < size; x += 8) {
      ctx.lineTo(x, y + Math.sin(x * 0.03 + i * 2) * 4);
    }
    ctx.stroke();
  }

  // 300-400 noise specks
  const speckCount = 300 + Math.floor(Math.random() * 101);
  for (let i = 0; i < speckCount; i++) {
    const px = Math.random() * size;
    const py = Math.random() * size;
    const bright = Math.random() > 0.5;
    ctx.fillStyle = bright ? '#8888ff' : '#7878f0';
    ctx.fillRect(px, py, 1.5, 1.5);
  }

  tex.update();
  tex.wrapU = Constants.TEXTURE_WRAP_ADDRESSMODE;
  tex.wrapV = Constants.TEXTURE_WRAP_ADDRESSMODE;
  return tex;
}


/**
 * Creates a leaf cluster texture with bezier-curve leaf shapes on transparent background.
 * @param {'outer'|'inner'} colorVariant — 'outer' for bright canopy, 'inner' for shaded interior
 */
function createLeafClusterTexture(name, scene, size = 512, colorVariant = 'outer') {
  const tex = new DynamicTexture(name, size, scene, true);
  const ctx = tex.getContext();

  // Transparent background
  ctx.clearRect(0, 0, size, size);

  // Color ranges based on variant — strong saturated greens for PBR
  const colorRange = colorVariant === 'inner'
    ? { rMin: 25, rMax: 50, gMin: 110, gMax: 160, bMin: 15, bMax: 35 }
    : { rMin: 45, rMax: 80, gMin: 170, gMax: 230, bMin: 30, bMax: 55 };

  // Helper: draw a single leaf at (0,0) pointing up, with length leafLen
  function drawLeaf(leafLen, leafWidth) {
    const hw = leafWidth || leafLen * 0.3;

    // Shadow offset
    ctx.save();
    ctx.translate(2, 2);
    ctx.beginPath();
    ctx.moveTo(0, -leafLen / 2);
    ctx.bezierCurveTo(hw, -leafLen * 0.2, hw, leafLen * 0.2, 0, leafLen / 2);
    ctx.bezierCurveTo(-hw, leafLen * 0.2, -hw, -leafLen * 0.2, 0, -leafLen / 2);
    ctx.fillStyle = 'rgba(0,0,0,0.08)';
    ctx.fill();
    ctx.restore();

    // Leaf body
    const r = colorRange.rMin + Math.random() * (colorRange.rMax - colorRange.rMin);
    const g = colorRange.gMin + Math.random() * (colorRange.gMax - colorRange.gMin);
    const b = colorRange.bMin + Math.random() * (colorRange.bMax - colorRange.bMin);

    ctx.beginPath();
    ctx.moveTo(0, -leafLen / 2);
    ctx.bezierCurveTo(hw, -leafLen * 0.2, hw, leafLen * 0.2, 0, leafLen / 2);
    ctx.bezierCurveTo(-hw, leafLen * 0.2, -hw, -leafLen * 0.2, 0, -leafLen / 2);
    ctx.fillStyle = `rgb(${r | 0}, ${g | 0}, ${b | 0})`;
    ctx.fill();

    // Midrib
    ctx.beginPath();
    ctx.moveTo(0, -leafLen / 2);
    ctx.lineTo(0, leafLen / 2);
    ctx.strokeStyle = `rgba(${(r - 15) | 0}, ${(g + 10) | 0}, ${(b - 5) | 0}, 0.6)`;
    ctx.lineWidth = 1;
    ctx.stroke();

    // Side veins: 2 branching lines from ~40% along midrib
    const veinY = -leafLen / 2 + leafLen * 0.4;
    const veinAngle = 30 * Math.PI / 180;
    const veinLen = hw * 0.7;
    ctx.strokeStyle = `rgba(${(r - 10) | 0}, ${(g + 5) | 0}, ${(b - 3) | 0}, 0.3)`;
    ctx.lineWidth = 0.7;
    for (const sign of [-1, 1]) {
      ctx.beginPath();
      ctx.moveTo(0, veinY);
      ctx.lineTo(sign * Math.sin(veinAngle) * veinLen, veinY + Math.cos(veinAngle) * veinLen);
      ctx.stroke();
    }
  }

  // 3-tier leaf sizes: 8-10 large, 10-14 medium, 6-8 small = 24-32 total
  const tiers = [
    { count: 8 + Math.floor(Math.random() * 3), minLen: 55, maxLen: 70 },
    { count: 10 + Math.floor(Math.random() * 5), minLen: 35, maxLen: 50 },
    { count: 6 + Math.floor(Math.random() * 3), minLen: 20, maxLen: 30 },
  ];

  for (const tier of tiers) {
    for (let i = 0; i < tier.count; i++) {
      const cx = size * 0.08 + Math.random() * size * 0.84;
      const cy = size * 0.08 + Math.random() * size * 0.84;
      const leafLen = tier.minLen + Math.random() * (tier.maxLen - tier.minLen);
      const angle = Math.random() * Math.PI * 2;

      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(angle);
      drawLeaf(leafLen);
      ctx.restore();
    }
  }

  tex.update();
  tex.hasAlpha = true;
  tex.wrapU = Constants.TEXTURE_WRAP_ADDRESSMODE;
  tex.wrapV = Constants.TEXTURE_WRAP_ADDRESSMODE;
  return tex;
}

/**
 * Procedural soil texture — pixel-noise dark reddish-brown with soft splotches.
 */
function createSoilTexture(name, scene, size = 512) {
  const tex = new DynamicTexture(name, size, scene, false);
  const ctx = tex.getContext();

  let s = 77731;
  const rand = () => { s = (s * 16807) % 2147483647; return (s - 1) / 2147483646; };

  // Fill pixel-by-pixel for uniform fine-grained soil
  const imgData = ctx.createImageData(size, size);
  const d = imgData.data;
  for (let i = 0; i < size * size; i++) {
    const v = rand();
    // Dark reddish-brown base with per-pixel variation
    const r = Math.floor(45 + v * 40 + rand() * 15);
    const g = Math.floor(22 + v * 18 + rand() * 8);
    const b = Math.floor(10 + v * 10 + rand() * 5);
    d[i * 4]     = r;
    d[i * 4 + 1] = g;
    d[i * 4 + 2] = b;
    d[i * 4 + 3] = 255;
  }
  ctx.putImageData(imgData, 0, 0);

  // Soft splotches for subtle variation (not sharp shapes)
  for (let i = 0; i < 30; i++) {
    const x = rand() * size;
    const y = rand() * size;
    const r = 20 + rand() * 60;
    const bright = rand() > 0.5;
    const a = 0.06 + rand() * 0.08;
    const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
    if (bright) {
      grad.addColorStop(0, `rgba(90,50,25,${a})`);
    } else {
      grad.addColorStop(0, `rgba(20,10,5,${a})`);
    }
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(x - r, y - r, r * 2, r * 2);
  }

  tex.update();
  return tex;
}

function createGrassClumpTexture(name, scene, size = 256) {
  const tex = new DynamicTexture(name, size, scene, true);
  const ctx = tex.getContext();
  ctx.clearRect(0, 0, size, size);

  // Deterministic PRNG
  let s = 12345;
  const rand = () => { s = (s * 16807) % 2147483647; return (s - 1) / 2147483646; };

  const bladeCount = 8;
  for (let i = 0; i < bladeCount; i++) {
    const baseX = size * (0.08 + rand() * 0.84);
    const height = size * (0.50 + rand() * 0.45);
    const tipX = baseX + (rand() - 0.5) * size * 0.30;
    const cpX = baseX + (tipX - baseX) * 0.5 + (rand() - 0.5) * 20;
    const cpY = size - height * 0.6;
    const bladeW = 4 + rand() * 5;

    const gr = 90 + Math.floor(rand() * 80);
    const rr = 25 + Math.floor(rand() * 40);
    const bb = 8 + Math.floor(rand() * 25);

    // Draw blade as tapered bezier segments (base → tip)
    const steps = 10;
    for (let j = 0; j < steps; j++) {
      const t0 = j / steps;
      const t1 = (j + 1) / steps;
      const mt0 = 1 - t0, mt1 = 1 - t1;

      const x0 = mt0 * mt0 * baseX + 2 * mt0 * t0 * cpX + t0 * t0 * tipX;
      const y0 = mt0 * mt0 * size + 2 * mt0 * t0 * cpY + t0 * t0 * (size - height);
      const x1 = mt1 * mt1 * baseX + 2 * mt1 * t1 * cpX + t1 * t1 * tipX;
      const y1 = mt1 * mt1 * size + 2 * mt1 * t1 * cpY + t1 * t1 * (size - height);

      const w = bladeW * (1 - t0 * 0.7);
      const bright = 1 + t0 * 0.6;
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x1, y1);
      ctx.lineWidth = w;
      ctx.strokeStyle = `rgba(${Math.min(255, Math.floor(rr * bright))},${Math.min(255, Math.floor(gr * bright))},${Math.min(255, Math.floor(bb * bright))},1)`;
      ctx.lineCap = 'round';
      ctx.stroke();
    }
  }

  tex.update();
  tex.hasAlpha = true;
  return tex;
}

/**
 * Procedural dandelion texture — draws 3-4 stems with yellow flower heads
 * and white seed puffs on transparent background.
 */
function createDandelionTexture(name, scene, size = 512) {
  const tex = new DynamicTexture(name, size, scene, true);
  const ctx = tex.getContext();
  ctx.clearRect(0, 0, size, size);

  let s = 54321;
  const rand = () => { s = (s * 16807) % 2147483647; return (s - 1) / 2147483646; };

  // Draw 1-2 big prominent stems with very large flower heads
  const stemCount = 1 + Math.floor(rand() * 2); // 1-2 stems
  for (let i = 0; i < stemCount; i++) {
    const baseX = size * (0.3 + i * 0.4 + (rand() - 0.5) * 0.1);
    const stemH = size * (0.55 + rand() * 0.3);
    const tipX = baseX + (rand() - 0.5) * size * 0.08;
    const tipY = size - stemH;

    // Thick green stem
    ctx.beginPath();
    ctx.moveTo(baseX, size);
    const cpX = baseX + (tipX - baseX) * 0.4 + (rand() - 0.5) * 10;
    const cpY = size - stemH * 0.5;
    ctx.quadraticCurveTo(cpX, cpY, tipX, tipY);
    ctx.strokeStyle = `rgb(${55 + Math.floor(rand() * 25)},${110 + Math.floor(rand() * 30)},${30 + Math.floor(rand() * 15)})`;
    ctx.lineWidth = 5 + rand() * 3;
    ctx.stroke();

    // Jagged dandelion leaves at base
    if (rand() > 0.3) {
      const leafY = size - stemH * (0.15 + rand() * 0.2);
      const leafX = baseX + (tipX - baseX) * 0.15;
      for (let side = -1; side <= 1; side += 2) {
        ctx.beginPath();
        ctx.moveTo(leafX, leafY);
        const leafLen = 20 + rand() * 20;
        const lobes = 3 + Math.floor(rand() * 2);
        for (let l = 0; l < lobes; l++) {
          const t = (l + 1) / lobes;
          const lx = leafX + side * leafLen * t;
          const ly = leafY - 4 * Math.sin(t * Math.PI) + (rand() - 0.5) * 4;
          const notchX = leafX + side * leafLen * (t - 0.3 / lobes);
          const notchY = leafY + 3;
          ctx.lineTo(lx, ly);
          if (l < lobes - 1) ctx.lineTo(notchX, notchY);
        }
        ctx.lineTo(leafX, leafY + 2);
        ctx.closePath();
        ctx.fillStyle = `rgb(${50 + Math.floor(rand() * 20)},${100 + Math.floor(rand() * 30)},${25 + Math.floor(rand() * 12)})`;
        ctx.fill();
      }
    }

    // Large flower head
    const isYellow = rand() > 0.35; // 65% yellow, 35% white puff
    const headR = 30 + rand() * 25; // prominent: 30-55px radius

    if (isYellow) {
      // Yellow dandelion — dense petal disc
      // Outer petals layer
      const petalCount = 22 + Math.floor(rand() * 10);
      for (let p = 0; p < petalCount; p++) {
        const angle = (p / petalCount) * Math.PI * 2 + rand() * 0.2;
        const pLen = headR * (0.8 + rand() * 0.3);
        const px = tipX + Math.cos(angle) * pLen;
        const py = tipY + Math.sin(angle) * pLen;
        // Petal as thick rounded line
        ctx.beginPath();
        ctx.moveTo(tipX + Math.cos(angle) * headR * 0.15, tipY + Math.sin(angle) * headR * 0.15);
        ctx.lineTo(px, py);
        ctx.lineWidth = 4 + rand() * 4;
        ctx.lineCap = 'round';
        const yb = 210 + Math.floor(rand() * 45);
        ctx.strokeStyle = `rgb(${yb},${Math.floor(yb * 0.82)},${10 + Math.floor(rand() * 15)})`;
        ctx.stroke();
      }
      // Inner petals — shorter, denser
      const innerCount = 12 + Math.floor(rand() * 6);
      for (let p = 0; p < innerCount; p++) {
        const angle = (p / innerCount) * Math.PI * 2 + rand() * 0.4;
        const pLen = headR * (0.3 + rand() * 0.35);
        const px = tipX + Math.cos(angle) * pLen;
        const py = tipY + Math.sin(angle) * pLen;
        ctx.beginPath();
        ctx.moveTo(tipX, tipY);
        ctx.lineTo(px, py);
        ctx.lineWidth = 3 + rand() * 3;
        ctx.lineCap = 'round';
        ctx.strokeStyle = `rgb(${230 + Math.floor(rand() * 25)},${190 + Math.floor(rand() * 40)},${15 + Math.floor(rand() * 20)})`;
        ctx.stroke();
      }
      // Bright center disc
      ctx.beginPath();
      ctx.arc(tipX, tipY, headR * 0.25, 0, Math.PI * 2);
      ctx.fillStyle = `rgb(${230 + Math.floor(rand() * 25)},${195 + Math.floor(rand() * 30)},25)`;
      ctx.fill();
    } else {
      // White seed puff — large dense fluffy sphere
      // Solid white core first
      ctx.beginPath();
      ctx.arc(tipX, tipY, headR * 0.45, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(235,235,230,0.5)';
      ctx.fill();
      const puffLines = 50 + Math.floor(rand() * 20);
      for (let p = 0; p < puffLines; p++) {
        const angle = (p / puffLines) * Math.PI * 2 + rand() * 0.6;
        const pLen = headR * (0.4 + rand() * 0.65);
        const px = tipX + Math.cos(angle) * pLen;
        const py = tipY + Math.sin(angle) * pLen;
        // Thick filament line
        ctx.beginPath();
        ctx.moveTo(tipX + Math.cos(angle) * 4, tipY + Math.sin(angle) * 4);
        ctx.lineTo(px, py);
        ctx.lineWidth = 1.5 + rand() * 2;
        ctx.lineCap = 'round';
        const wb = 220 + Math.floor(rand() * 35);
        ctx.strokeStyle = `rgba(${wb},${wb},${wb - 5},${0.8 + rand() * 0.2})`;
        ctx.stroke();
        // Seed tuft at tip — bigger dot
        ctx.beginPath();
        ctx.arc(px, py, 2.5 + rand() * 3, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${wb + 10},${wb + 10},${wb},0.9)`;
        ctx.fill();
      }
      // Gray-brown center sphere
      ctx.beginPath();
      ctx.arc(tipX, tipY, headR * 0.2, 0, Math.PI * 2);
      ctx.fillStyle = `rgb(${180 + Math.floor(rand() * 30)},${175 + Math.floor(rand() * 25)},${165 + Math.floor(rand() * 25)})`;
      ctx.fill();
    }
  }

  tex.update();
  tex.hasAlpha = true;
  return tex;
}


/**
 * Creates a procedural fountain grass (Pennisetum) texture — tall arching blades
 * with fuzzy elongated plume/seed heads at the tips.
 */
function createFountainGrassTexture(name, scene, size = 512) {
  const tex = new DynamicTexture(name, size, scene, true);
  const ctx = tex.getContext();
  ctx.clearRect(0, 0, size, size);

  let s = 98765;
  const rand = () => { s = (s * 16807) % 2147483647; return (s - 1) / 2147483646; };

  // Helper: draw a thick curved blade from (bx,by) to (tx,ty)
  function drawBlade(bx, by, tx, ty, baseW, tipW, r, g, b) {
    const segs = 16;
    const cpx = bx + (tx - bx) * 0.4 + (rand() - 0.5) * 15;
    const cpy = by + (ty - by) * 0.5;
    for (let i = 0; i < segs; i++) {
      const t0 = i / segs, t1 = (i + 1) / segs;
      const x0 = (1-t0)*(1-t0)*bx + 2*(1-t0)*t0*cpx + t0*t0*tx;
      const y0 = (1-t0)*(1-t0)*by + 2*(1-t0)*t0*cpy + t0*t0*ty;
      const x1 = (1-t1)*(1-t1)*bx + 2*(1-t1)*t1*cpx + t1*t1*tx;
      const y1 = (1-t1)*(1-t1)*by + 2*(1-t1)*t1*cpy + t1*t1*ty;
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x1, y1);
      ctx.lineWidth = baseW + (tipW - baseW) * t0;
      const rv = Math.floor(rand() * 12);
      ctx.strokeStyle = `rgb(${r + rv},${g + rv},${b + rv})`;
      ctx.lineCap = 'round';
      ctx.stroke();
    }
  }

  // Dense base cluster: 8-12 thick green blades arching outward like a fountain
  const baseX = size * 0.5;
  const baseY = size;
  const bladeCount = 8 + Math.floor(rand() * 5);
  for (let i = 0; i < bladeCount; i++) {
    const spread = (i / bladeCount - 0.5) * 2; // -1 to 1
    const bladeH = size * (0.35 + rand() * 0.3);
    // Strong arch — blades curve outward and droop like a fountain
    const archX = baseX + spread * size * 0.35 + (rand() - 0.5) * 15;
    const tipY = size - bladeH + Math.abs(spread) * size * 0.1; // tips droop at edges
    const gr = 50 + Math.floor(rand() * 25);
    const gg = 85 + Math.floor(rand() * 35);
    const gb = 22 + Math.floor(rand() * 12);
    drawBlade(baseX + (rand() - 0.5) * 10, baseY, archX, tipY, 6 + rand() * 3, 1.5 + rand(), gr, gg, gb);
  }

  // 2-3 plume stems — curve upward then droop like real Pennisetum
  const plumeCount = 2 + Math.floor(rand() * 2);
  for (let p = 0; p < plumeCount; p++) {
    const stemBaseX = baseX + (rand() - 0.5) * 16;
    const stemH = size * (0.55 + rand() * 0.25);
    // Physics-like curve: stem rises then bends/droops to one side
    const bendDir = (rand() - 0.5) * 2; // strong lateral lean
    const stemTipX = stemBaseX + bendDir * size * 0.15;
    const stemTipY = size - stemH + Math.abs(bendDir) * size * 0.06; // droop from weight

    // Draw curved stem with S-bend
    const stemSegs = 20;
    const stemCpx = stemBaseX + bendDir * size * 0.04;
    const stemCpy = baseY - stemH * 0.5;
    for (let i = 0; i < stemSegs; i++) {
      const t0 = i / stemSegs, t1 = (i + 1) / stemSegs;
      const x0 = (1-t0)*(1-t0)*stemBaseX + 2*(1-t0)*t0*stemCpx + t0*t0*stemTipX;
      const y0 = (1-t0)*(1-t0)*baseY + 2*(1-t0)*t0*stemCpy + t0*t0*stemTipY;
      const x1 = (1-t1)*(1-t1)*stemBaseX + 2*(1-t1)*t1*stemCpx + t1*t1*stemTipX;
      const y1 = (1-t1)*(1-t1)*baseY + 2*(1-t1)*t1*stemCpy + t1*t1*stemTipY;
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x1, y1);
      ctx.lineWidth = 4 * (1 - t0 * 0.6) + rand() * 0.5;
      ctx.strokeStyle = `rgb(${55 + Math.floor(rand()*15)},${90 + Math.floor(rand()*20)},${28 + Math.floor(rand()*10)})`;
      ctx.lineCap = 'round';
      ctx.stroke();
    }

    // Large fluffy plume at tip — fat fuzzy ellipse that droops slightly
    const plumeLen = 45 + rand() * 40;
    const plumeW = 22 + rand() * 16;
    // Plume droops in the bend direction
    const plumeCX = stemTipX + bendDir * plumeLen * 0.1;
    const plumeCY = stemTipY + plumeLen * 0.35;
    // Plume rotation angle follows the stem's droop
    const plumeAngle = Math.atan2(bendDir * 0.3, -1) + Math.PI / 2;

    // Solid fluffy core
    ctx.save();
    ctx.translate(plumeCX, plumeCY);
    ctx.rotate(plumeAngle * 0.3);
    ctx.beginPath();
    ctx.ellipse(0, 0, plumeW * 0.5, plumeLen * 0.4, 0, 0, Math.PI * 2);
    const coreR = 200 + Math.floor(rand() * 40);
    const coreG = 185 + Math.floor(rand() * 35);
    const coreB = 145 + Math.floor(rand() * 35);
    ctx.fillStyle = `rgba(${coreR},${coreG},${coreB},0.55)`;
    ctx.fill();
    ctx.restore();

    // Dense fluffy filaments — white/cream, thicker, more prominent
    const filaments = 100 + Math.floor(rand() * 50);
    for (let f = 0; f < filaments; f++) {
      const t = rand();
      // Along the plume spine (drooping)
      const spineY = stemTipY - plumeLen * 0.1 + t * plumeLen * 0.7;
      const spineX = stemTipX + t * bendDir * plumeLen * 0.15 + (rand() - 0.5) * 3;
      const side = rand() > 0.5 ? 1 : -1;
      const fAngle = side * (0.3 + rand() * 1.0) + plumeAngle * 0.2;
      const widthFactor = 1 - Math.abs(t - 0.4) * 1.4;
      const fLen = plumeW * (0.35 + rand() * 0.5) * Math.max(0.15, widthFactor);
      const fx = spineX + Math.cos(fAngle) * fLen;
      const fy = spineY + Math.sin(fAngle) * fLen * 0.35 + rand() * 2;

      ctx.beginPath();
      ctx.moveTo(spineX, spineY);
      ctx.lineTo(fx, fy);
      ctx.lineWidth = 1.5 + rand() * 2.5;
      ctx.lineCap = 'round';
      // White/cream/light tan — brighter, fluffier look
      const cr = 210 + Math.floor(rand() * 45);
      const cg = 195 + Math.floor(rand() * 45);
      const cb = 160 + Math.floor(rand() * 50);
      ctx.strokeStyle = `rgba(${cr},${cg},${cb},${0.65 + rand() * 0.35})`;
      ctx.stroke();

      // Fluffy seed dot at many tips
      if (rand() > 0.3) {
        ctx.beginPath();
        ctx.arc(fx, fy, 1.5 + rand() * 2.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${cr + 15},${cg + 15},${cb + 10},0.85)`;
        ctx.fill();
      }
    }
  }

  tex.update();
  tex.hasAlpha = true;
  return tex;
}

export function createMaterials(scene) {
  const mats = {};

  // ── FLOOR TILES ──
  const tileAlbedo = createTileTexture('tileTex', scene, 60, 60, '#d9d0c3', '#b0a898');
  tileAlbedo.uScale = 8;
  tileAlbedo.vScale = 8;
  const tileBump = createBumpFromTile('tileBump', scene, 60, 60);
  tileBump.uScale = 8;
  tileBump.vScale = 8;

  mats.vitrifiedTile = new PBRMaterial('vitrifiedTile', scene);
  mats.vitrifiedTile.albedoTexture = tileAlbedo;
  mats.vitrifiedTile.bumpTexture = tileBump;
  mats.vitrifiedTile.bumpTexture.level = 0.3;
  mats.vitrifiedTile.metallic = 0.05;
  mats.vitrifiedTile.roughness = 0.22;
  mats.vitrifiedTile.enableSpecularAntiAliasing = true;

  // Octagon tile — white octagons with gray accent squares
  const octTileAlbedo = createOctagonTileTexture('octTileTex', scene, 170, 512);
  octTileAlbedo.uScale = 0.06;
  octTileAlbedo.vScale = 0.06;
  mats.octagonTile = new PBRMaterial('octagonTile', scene);
  mats.octagonTile.albedoTexture = octTileAlbedo;
  mats.octagonTile.metallic = 0.05;
  mats.octagonTile.roughness = 0.25;
  mats.octagonTile.enableSpecularAntiAliasing = true;

  // Designer tiles (foyer/passages) — darker, polished
  const designerAlbedo = createTileTexture('designerTex', scene, 80, 80, '#c4b89e', '#a09080');
  designerAlbedo.uScale = 6;
  designerAlbedo.vScale = 6;

  mats.designerTile = new PBRMaterial('designerTile', scene);
  mats.designerTile.albedoTexture = designerAlbedo;
  mats.designerTile.metallic = 0.03;
  mats.designerTile.roughness = 0.18;

  // Concrete (basement)
  mats.concrete = new PBRMaterial('concrete', scene);
  mats.concrete.albedoColor = new Color3(0.52, 0.50, 0.48);
  mats.concrete.metallic = 0.0;
  mats.concrete.roughness = 0.88;

  // ── WALLS ──
  mats.wallInterior = new PBRMaterial('wallInterior', scene);
  mats.wallInterior.albedoColor = new Color3(0.90, 0.85, 0.76);
  mats.wallInterior.metallic = 0.0;
  mats.wallInterior.roughness = 0.82;

  // Exterior cladding — dark charcoal with subtle metallic
  mats.wallExterior = new PBRMaterial('wallExterior', scene);
  mats.wallExterior.albedoColor = new Color3(0.12, 0.13, 0.15);
  mats.wallExterior.metallic = 0.35;
  mats.wallExterior.roughness = 0.35;

  // ── GLASS (upper floors — soft steel-blue, semi-transparent like reference) ──
  mats.glass = new PBRMaterial('glass', scene);
  mats.glass.albedoColor = new Color3(0.02, 0.1, 0.95);
  mats.glass.metallic = 0.15;
  mats.glass.roughness = 0.05;
  mats.glass.alpha = 0.5;
  mats.glass.transparencyMode = 2;
  mats.glass.backFaceCulling = false;
  mats.glass.reflectionColor = new Color3(0.05, 0.15, 0.92);
  mats.glass.environmentIntensity = 1.8;
  mats.glass.directIntensity = 0.5;
  mats.glass.microSurface = 0.95;

  // Glass (ground floor showroom — same blue, slightly more transparent)
  mats.glassShowroom = new PBRMaterial('glassShowroom', scene);
  mats.glassShowroom.albedoColor = new Color3(0.02, 0.1, 0.95);
  mats.glassShowroom.metallic = 0.15;
  mats.glassShowroom.roughness = 0.05;
  mats.glassShowroom.alpha = 0.4;
  mats.glassShowroom.transparencyMode = 2;
  mats.glassShowroom.backFaceCulling = false;
  mats.glassShowroom.reflectionColor = new Color3(0.05, 0.15, 0.92);
  mats.glassShowroom.environmentIntensity = 1.8;
  mats.glassShowroom.directIntensity = 0.5;
  mats.glassShowroom.microSurface = 0.95;

  // Glass (interior partition — clearer)
  mats.glassInterior = new PBRMaterial('glassInterior', scene);
  mats.glassInterior.albedoColor = new Color3(0.85, 0.9, 0.92);
  mats.glassInterior.metallic = 0.0;
  mats.glassInterior.roughness = 0.02;
  mats.glassInterior.alpha = 0.2;
  mats.glassInterior.transparencyMode = 2;
  mats.glassInterior.backFaceCulling = false;

  // Aluminium frame
  mats.aluminium = new PBRMaterial('aluminium', scene);
  mats.aluminium.albedoColor = new Color3(0.55, 0.57, 0.60);
  mats.aluminium.metallic = 0.9;
  mats.aluminium.roughness = 0.25;

  // Dark aluminium (mullions, window frames)
  mats.darkAluminium = new PBRMaterial('darkAluminium', scene);
  mats.darkAluminium.albedoColor = new Color3(0.15, 0.16, 0.18);
  mats.darkAluminium.metallic = 0.85;
  mats.darkAluminium.roughness = 0.3;

  // ── SPECIAL SURFACES ──

  // Marble (reception, lobby)
  const marbleTex = createMarbleTexture('marbleTex', scene);
  marbleTex.uScale = 4;
  marbleTex.vScale = 4;
  mats.marble = new PBRMaterial('marble', scene);
  mats.marble.albedoTexture = marbleTex;
  mats.marble.metallic = 0.05;
  mats.marble.roughness = 0.12;
  mats.marble.enableSpecularAntiAliasing = true;

  // Wood (doors, desks, accents)
  const woodTex = createWoodTexture('woodTex', scene);
  woodTex.uScale = 2;
  woodTex.vScale = 2;
  mats.wood = new PBRMaterial('wood', scene);
  mats.wood.albedoTexture = woodTex;
  mats.wood.metallic = 0.0;
  mats.wood.roughness = 0.45;

  // Dark wood (premium furniture)
  mats.darkWood = new PBRMaterial('darkWood', scene);
  mats.darkWood.albedoColor = new Color3(0.22, 0.13, 0.06);
  mats.darkWood.metallic = 0.0;
  mats.darkWood.roughness = 0.35;

  // Ceiling (white with subtle grid)
  mats.ceiling = new PBRMaterial('ceiling', scene);
  mats.ceiling.albedoColor = new Color3(0.95, 0.95, 0.95);
  mats.ceiling.metallic = 0.0;
  mats.ceiling.roughness = 0.9;

  // Fabric (chairs, sofas)
  mats.fabricGray = new PBRMaterial('fabricGray', scene);
  mats.fabricGray.albedoColor = new Color3(0.35, 0.35, 0.38);
  mats.fabricGray.metallic = 0.0;
  mats.fabricGray.roughness = 0.85;
  mats.fabricGray.sheen.isEnabled = true;
  mats.fabricGray.sheen.intensity = 0.3;
  mats.fabricGray.sheen.color = new Color3(0.5, 0.5, 0.55);

  // Leather (premium seats)
  mats.leather = new PBRMaterial('leather', scene);
  mats.leather.albedoColor = new Color3(0.15, 0.10, 0.06);
  mats.leather.metallic = 0.0;
  mats.leather.roughness = 0.5;

  // Chrome (chair legs, fixtures)
  mats.chrome = new PBRMaterial('chrome', scene);
  mats.chrome.albedoColor = new Color3(0.75, 0.75, 0.78);
  mats.chrome.metallic = 0.95;
  mats.chrome.roughness = 0.25;

  // White plastic (desk accessories, light panels)
  mats.whitePlastic = new PBRMaterial('whitePlastic', scene);
  mats.whitePlastic.albedoColor = new Color3(0.92, 0.92, 0.92);
  mats.whitePlastic.metallic = 0.0;
  mats.whitePlastic.roughness = 0.6;

  // ── EXTERIOR ──

  // Road (rough black asphalt)
  mats.road = new PBRMaterial('road', scene);
  mats.road.albedoColor = Color3.White();
  mats.road.metallic = 0.0;
  mats.road.roughness = 0.95;
  mats.road.albedoTexture = createAsphaltTexture('roadTex', scene, '#2a2a2c', 512);
  mats.road.albedoTexture.uScale = 0.05;
  mats.road.albedoTexture.vScale = 0.05;

  // Sidewalk (footpath with paver texture)
  mats.sidewalk = new PBRMaterial('sidewalk', scene);
  mats.sidewalk.albedoColor = new Color3(0.68, 0.66, 0.62);
  mats.sidewalk.metallic = 0.0;
  mats.sidewalk.roughness = 0.75;

  // Curb (planters — cobblestone pebble mosaic)
  mats.curb = new PBRMaterial('curb', scene);
  mats.curb.albedoColor = Color3.White();
  mats.curb.metallic = 0.0;
  mats.curb.roughness = 0.8;
  mats.curb.albedoTexture = createCobblestoneTexture('curbTex', scene, 4, 512);
  mats.curb.albedoTexture.uScale = 0.25;
  mats.curb.albedoTexture.vScale = 0.25;

  // Grass (green ground surface)
  mats.grass = new PBRMaterial('grass', scene);
  mats.grass.albedoColor = new Color3(0.22, 0.48, 0.15);
  mats.grass.metallic = 0.0;
  mats.grass.roughness = 0.95;

  // Soil (flat planter top — under grass clumps)
  const soilTex = createSoilTexture('soilTex', scene, 512);
  mats.soil = new PBRMaterial('soil', scene);
  mats.soil.albedoTexture = soilTex;
  mats.soil.metallic = 0.0;
  mats.soil.roughness = 0.95;
  mats.soil.environmentIntensity = 0.3;

  // Grass blade clumps — alpha-cutout ShaderMaterial with procedural texture
  Effect.ShadersStore["grassVertexShader"] = `
    precision highp float;
    attribute vec3 position;
    attribute vec2 uv;
    attribute vec4 world0;
    attribute vec4 world1;
    attribute vec4 world2;
    attribute vec4 world3;
    uniform mat4 viewProjection;
    varying vec2 vUV;
    varying float vHeight;
    varying vec3 vWorldPos;
    void main() {
      mat4 finalWorld = mat4(world0, world1, world2, world3);
      vec4 worldPos = finalWorld * vec4(position, 1.0);
      gl_Position = viewProjection * worldPos;
      vUV = uv;
      vHeight = uv.y;
      vWorldPos = worldPos.xyz;
    }
  `;
  Effect.ShadersStore["grassFragmentShader"] = `
    precision highp float;
    uniform sampler2D grassTex;
    uniform vec3 cameraPosition;
    varying vec2 vUV;
    varying float vHeight;
    varying vec3 vWorldPos;
    void main() {
      vec4 texColor = texture2D(grassTex, vUV);
      // Distance-based alpha threshold: lower at distance so grass stays visible
      float dist = length(cameraPosition - vWorldPos);
      float threshold = mix(0.4, 0.05, clamp((dist - 30.0) / 170.0, 0.0, 1.0));
      if (texColor.a < threshold) discard;
      vec3 baseGreen = vec3(0.06, 0.18, 0.03);
      vec3 tipGreen = vec3(0.22, 0.52, 0.10);
      vec3 color = mix(baseGreen, tipGreen, vHeight);
      color *= smoothstep(0.0, 0.3, vHeight) * 0.6 + 0.4;
      float noise = fract(sin(dot(floor(vWorldPos.xz * 2.0), vec2(12.9898, 78.233))) * 43758.5453);
      color *= 0.80 + 0.4 * noise;
      color *= 0.55 + 0.45 * dot(vec3(0.0, 1.0, 0.0), normalize(vec3(0.3, 1.0, 0.2)));
      gl_FragColor = vec4(color, 1.0);
    }
  `;
  const grassClumpTex = createGrassClumpTexture('grassClumpTex', scene, 256);
  mats.grassBlade = new ShaderMaterial('grassBlade', scene,
    { vertex: "grass", fragment: "grass" },
    {
      attributes: ["position", "uv", "world0", "world1", "world2", "world3"],
      uniforms: ["viewProjection", "cameraPosition"],
      samplers: ["grassTex"],
      needAlphaTesting: true,
    }
  );
  mats.grassBlade.setTexture("grassTex", grassClumpTex);
  mats.grassBlade.backFaceCulling = false;
  mats.grassBlade.onBind = (mesh) => {
    const cam = scene.activeCamera;
    if (cam) {
      mats.grassBlade.getEffect().setVector3("cameraPosition", cam.position);
    }
  };

  // Dandelion flower clumps — alpha-cutout ShaderMaterial (uses texture color directly)
  Effect.ShadersStore["flowerVertexShader"] = Effect.ShadersStore["grassVertexShader"];
  Effect.ShadersStore["flowerFragmentShader"] = `
    precision highp float;
    uniform sampler2D grassTex;
    uniform vec3 cameraPosition;
    varying vec2 vUV;
    varying float vHeight;
    varying vec3 vWorldPos;
    void main() {
      vec4 texColor = texture2D(grassTex, vUV);
      float dist = length(cameraPosition - vWorldPos);
      float threshold = mix(0.4, 0.05, clamp((dist - 30.0) / 170.0, 0.0, 1.0));
      if (texColor.a < threshold) discard;
      vec3 color = texColor.rgb;
      color *= 0.6 + 0.4 * dot(vec3(0.0, 1.0, 0.0), normalize(vec3(0.3, 1.0, 0.2)));
      gl_FragColor = vec4(color, 1.0);
    }
  `;
  const dandelionTex = createDandelionTexture('dandelionTex', scene, 512);
  mats.flowerBlade = new ShaderMaterial('flowerBlade', scene,
    { vertex: "flower", fragment: "flower" },
    {
      attributes: ["position", "uv", "world0", "world1", "world2", "world3"],
      uniforms: ["viewProjection", "cameraPosition"],
      samplers: ["grassTex"],
      needAlphaTesting: true,
    }
  );
  mats.flowerBlade.setTexture("grassTex", dandelionTex);
  mats.flowerBlade.backFaceCulling = false;
  mats.flowerBlade.onBind = (mesh) => {
    const cam = scene.activeCamera;
    if (cam) {
      mats.flowerBlade.getEffect().setVector3("cameraPosition", cam.position);
    }
  };

  // Fountain grass (Pennisetum) — reuses flower shader (texture color direct)
  const fountainGrassTex = createFountainGrassTexture('fountainGrassTex', scene, 512);
  mats.fountainGrass = new ShaderMaterial('fountainGrass', scene,
    { vertex: "flower", fragment: "flower" },
    {
      attributes: ["position", "uv", "world0", "world1", "world2", "world3"],
      uniforms: ["viewProjection", "cameraPosition"],
      samplers: ["grassTex"],
      needAlphaTesting: true,
    }
  );
  mats.fountainGrass.setTexture("grassTex", fountainGrassTex);
  mats.fountainGrass.backFaceCulling = false;
  mats.fountainGrass.onBind = (mesh) => {
    const cam = scene.activeCamera;
    if (cam) {
      mats.fountainGrass.getEffect().setVector3("cameraPosition", cam.position);
    }
  };

  // Tree trunk (bark — procedural texture + bump)
  const barkTex = createBarkTexture('barkTex', scene, 512);
  barkTex.uScale = 2;
  barkTex.vScale = 3;
  const barkBumpTex = createBarkBumpTexture('barkBumpTex', scene, 512);
  barkBumpTex.uScale = 2;
  barkBumpTex.vScale = 3;
  mats.bark = new PBRMaterial('bark', scene);
  mats.bark.albedoTexture = barkTex;
  mats.bark.albedoColor = Color3.White();
  mats.bark.metallic = 0.0;
  mats.bark.roughness = 0.92;
  mats.bark.bumpTexture = barkBumpTex;
  mats.bark.bumpTexture.level = 0.4;

  // ── PALM TRUNK ──
  const palmTrunkTex = createPalmTrunkTexture('palmTrunkTex', scene, 512);
  palmTrunkTex.uScale = 1;
  palmTrunkTex.vScale = 4;
  const palmTrunkBumpTex = createPalmTrunkBumpTexture('palmTrunkBumpTex', scene, 512);
  palmTrunkBumpTex.uScale = 1;
  palmTrunkBumpTex.vScale = 4;
  mats.palmTrunk = new PBRMaterial('palmTrunk', scene);
  mats.palmTrunk.albedoTexture = palmTrunkTex;
  mats.palmTrunk.albedoColor = Color3.White();
  mats.palmTrunk.metallic = 0;
  mats.palmTrunk.roughness = 0.82;
  mats.palmTrunk.bumpTexture = palmTrunkBumpTex;
  mats.palmTrunk.bumpTexture.level = 0.5;

  // ── PALM FROND ──
  const palmFrondTex = createPalmFrondTexture('palmFrondTex', scene, 512);
  mats.palmFrond = new PBRMaterial('palmFrond', scene);
  mats.palmFrond.albedoTexture = palmFrondTex;
  mats.palmFrond.opacityTexture = palmFrondTex;
  mats.palmFrond.albedoColor = Color3.White();
  mats.palmFrond.emissiveColor = new Color3(0.02, 0.05, 0.01);
  mats.palmFrond.reflectivityColor = Color3.Black();
  mats.palmFrond.transparencyMode = 1; // ALPHATEST
  mats.palmFrond.alphaCutOff = 0.30;
  mats.palmFrond.backFaceCulling = false;
  mats.palmFrond.twoSidedLighting = true;
  mats.palmFrond.metallic = 0;
  mats.palmFrond.roughness = 0.85;
  mats.palmFrond.environmentIntensity = 0.15;

  // Leaves (kept for terrace garden mini-shrubs)
  mats.leaves = new PBRMaterial('leaves', scene);
  mats.leaves.albedoColor = new Color3(0.18, 0.42, 0.12);
  mats.leaves.metallic = 0.0;
  mats.leaves.roughness = 0.8;
  mats.leaves.subSurface.isTranslucencyEnabled = true;
  mats.leaves.subSurface.translucencyIntensity = 0.3;

  // Leaves alpha — outer canopy (bright green)
  // albedoColor must be White so the green texture colors aren't darkened by multiplication.
  // reflectivityColor Black + low environmentIntensity prevents IBL washing out greens.
  const leafClusterTexOuter = createLeafClusterTexture('leafClusterTexOuter', scene, 512, 'outer');
  mats.leavesAlpha = new PBRMaterial('leavesAlpha', scene);
  mats.leavesAlpha.albedoTexture = leafClusterTexOuter;
  mats.leavesAlpha.opacityTexture = leafClusterTexOuter;
  mats.leavesAlpha.albedoColor = Color3.White();
  mats.leavesAlpha.emissiveColor = new Color3(0.01, 0.025, 0.005);
  mats.leavesAlpha.reflectivityColor = Color3.Black();
  mats.leavesAlpha.transparencyMode = 1; // ALPHATEST
  mats.leavesAlpha.alphaCutOff = 0.25;
  mats.leavesAlpha.backFaceCulling = false;
  mats.leavesAlpha.twoSidedLighting = true;
  mats.leavesAlpha.metallic = 0;
  mats.leavesAlpha.roughness = 0.88;
  mats.leavesAlpha.environmentIntensity = 0.08;

  // Leaves alpha — inner canopy (darker shaded)
  const leafClusterTexInner = createLeafClusterTexture('leafClusterTexInner', scene, 512, 'inner');
  mats.leavesAlphaInner = new PBRMaterial('leavesAlphaInner', scene);
  mats.leavesAlphaInner.albedoTexture = leafClusterTexInner;
  mats.leavesAlphaInner.opacityTexture = leafClusterTexInner;
  mats.leavesAlphaInner.albedoColor = Color3.White();
  mats.leavesAlphaInner.emissiveColor = new Color3(0.008, 0.02, 0.004);
  mats.leavesAlphaInner.reflectivityColor = Color3.Black();
  mats.leavesAlphaInner.transparencyMode = 1; // ALPHATEST
  mats.leavesAlphaInner.alphaCutOff = 0.25;
  mats.leavesAlphaInner.backFaceCulling = false;
  mats.leavesAlphaInner.twoSidedLighting = true;
  mats.leavesAlphaInner.metallic = 0;
  mats.leavesAlphaInner.roughness = 0.92;
  mats.leavesAlphaInner.environmentIntensity = 0.05;

  // Gold accent (brand color)
  mats.goldAccent = new PBRMaterial('goldAccent', scene);
  mats.goldAccent.albedoColor = new Color3(0.79, 0.66, 0.31);
  mats.goldAccent.metallic = 0.75;
  mats.goldAccent.roughness = 0.2;

  // Parking line
  mats.parkingLine = new PBRMaterial('parkingLine', scene);
  mats.parkingLine.albedoColor = new Color3(0.9, 0.9, 0.2);
  mats.parkingLine.metallic = 0.0;
  mats.parkingLine.roughness = 0.8;
  mats.parkingLine.emissiveColor = new Color3(0.08, 0.08, 0.01);

  // Road marking white
  mats.roadMarking = new PBRMaterial('roadMarking', scene);
  mats.roadMarking.albedoColor = new Color3(0.9, 0.9, 0.9);
  mats.roadMarking.metallic = 0.0;
  mats.roadMarking.roughness = 0.6;

  // Rolling shutter
  mats.shutter = new PBRMaterial('shutter', scene);
  mats.shutter.albedoColor = new Color3(0.48, 0.48, 0.50);
  mats.shutter.metallic = 0.7;
  mats.shutter.roughness = 0.45;

  // Highlight
  mats.highlight = new PBRMaterial('highlight', scene);
  mats.highlight.albedoColor = new Color3(0.79, 0.66, 0.31);
  mats.highlight.metallic = 0.1;
  mats.highlight.roughness = 0.3;
  mats.highlight.emissiveColor = new Color3(0.3, 0.25, 0.1);

  // Terrace
  mats.terrace = new PBRMaterial('terrace', scene);
  mats.terrace.albedoColor = new Color3(0.3, 0.52, 0.25);
  mats.terrace.metallic = 0.0;
  mats.terrace.roughness = 0.9;

  // Light panel (emissive ceiling light)
  mats.lightPanel = new PBRMaterial('lightPanel', scene);
  mats.lightPanel.albedoColor = new Color3(1, 0.97, 0.92);
  mats.lightPanel.metallic = 0.0;
  mats.lightPanel.roughness = 0.3;
  mats.lightPanel.emissiveColor = new Color3(0.9, 0.87, 0.8);
  mats.lightPanel.emissiveIntensity = 0.7;

  // Warm interior glow (for window backlit effect)
  mats.warmGlow = new PBRMaterial('warmGlow', scene);
  mats.warmGlow.albedoColor = new Color3(1, 0.92, 0.7);
  mats.warmGlow.emissiveColor = new Color3(0.4, 0.32, 0.15);
  mats.warmGlow.metallic = 0.0;
  mats.warmGlow.roughness = 1.0;
  mats.warmGlow.alpha = 0.15;
  mats.warmGlow.transparencyMode = 2;

  // ── FACADE-SPECIFIC MATERIALS ──

  // Spandrel panel (dark opaque band between floor glass ribbons)
  mats.spandrel = new PBRMaterial('spandrel', scene);
  mats.spandrel.albedoColor = new Color3(0.10, 0.11, 0.13);
  mats.spandrel.metallic = 0.2;
  mats.spandrel.roughness = 0.55;

  // Stone base plinth (warm tan/beige at ground level)
  mats.stoneBase = new PBRMaterial('stoneBase', scene);
  mats.stoneBase.albedoColor = new Color3(0.72, 0.62, 0.48);
  mats.stoneBase.metallic = 0.0;
  mats.stoneBase.roughness = 0.6;

  // Gray marble (platform & steps — cool gray-blue with dark veins)
  const grayMarbleTex = createGrayMarbleTexture('grayMarbleTex', scene, 512);
  grayMarbleTex.uScale = 0.04;
  grayMarbleTex.vScale = 0.04;
  mats.grayMarble = new PBRMaterial('grayMarble', scene);
  mats.grayMarble.albedoTexture = grayMarbleTex;
  mats.grayMarble.metallic = 0.05;
  mats.grayMarble.roughness = 0.18;
  mats.grayMarble.enableSpecularAntiAliasing = true;

  // Canopy soffit — warm gold/bronze underside
  mats.canopySoffit = new PBRMaterial('canopySoffit', scene);
  mats.canopySoffit.albedoColor = new Color3(0.75, 0.58, 0.28);
  mats.canopySoffit.metallic = 0.65;
  mats.canopySoffit.roughness = 0.25;
  mats.canopySoffit.emissiveColor = new Color3(0.12, 0.08, 0.02);

  // Signage board (dark panel above showrooms)
  mats.signageBoard = new PBRMaterial('signageBoard', scene);
  mats.signageBoard.albedoColor = new Color3(0.08, 0.08, 0.10);
  mats.signageBoard.metallic = 0.1;
  mats.signageBoard.roughness = 0.7;

  // Signage text (light emissive for brand names)
  mats.signageText = new PBRMaterial('signageText', scene);
  mats.signageText.albedoColor = new Color3(0.95, 0.95, 0.95);
  mats.signageText.emissiveColor = new Color3(0.6, 0.55, 0.45);
  mats.signageText.metallic = 0.0;
  mats.signageText.roughness = 0.5;

  // ── PARKING & GROUND PAD ──

  // Parking surface — paver texture with visible brick pattern
  mats.parkingSurface = new PBRMaterial('parkingSurface', scene);
  mats.parkingSurface.albedoColor = Color3.White();
  mats.parkingSurface.metallic = 0.0;
  mats.parkingSurface.roughness = 0.90;
  mats.parkingSurface.albedoTexture = createPaverTexture(
    'parkingSurfaceTex', scene, 40, 20, '#706860', '#484040', 512
  );
  mats.parkingSurface.albedoTexture.uScale = 0.05;
  mats.parkingSurface.albedoTexture.vScale = 0.05;

  // Ground pad — light orange herringbone cross-brick paver
  mats.groundPadPaver = new PBRMaterial('groundPadPaver', scene);
  mats.groundPadPaver.albedoColor = Color3.White();
  mats.groundPadPaver.metallic = 0.0;
  mats.groundPadPaver.roughness = 0.78;
  mats.groundPadPaver.albedoTexture = createCrossBrickTexture(
    'groundPadTex', scene, 48, 2, '#d4a878', '#b08860', 512
  );
  mats.groundPadPaver.albedoTexture.uScale = 0.05;
  mats.groundPadPaver.albedoTexture.vScale = 0.05;

  return mats;
}
